import { css, type Theme } from '@emotion/react';
import { AppWindow, ArrowSquareOut } from '@phosphor-icons/react';
import { toTitleCase } from 'common/commonUtil';
import { EventType } from 'common/EventConstants';
import type { HostValue } from 'common/stack/ComponentWithValuesDocuments';
import { useApi } from 'client/lib/trpc';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { ViewAppBlocker } from 'client/pages/deployment/components/ViewApp/ViewAppBlocker';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';

interface ComponentHostProps {
  stackComponentID: number;
  host: HostValue;
}

export const ComponentHost = ({ stackComponentID, host }: ComponentHostProps) => {
  const api = useApi();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);

  const href = `https://${host.customDomain || host.tweakedDomain || host.defaultDomain || ''}`;

  const handleOpen = () => {
    if (deploymentID) {
      api.status.eventCreate.mutate({
        eventTypeID: EventType.DEPLOYMENT_HOST_OPEN,
        deploymentID,
        host: `${stackComponentID}-${host.identifier}`,
        reason: 'open',
      });
    }
  };

  return (
    <>
      <Typography variant="body2" css={titleStyle}>
        <AppWindow weight="bold" /> {toTitleCase(host.identifier)}
      </Typography>
      <ViewAppBlocker host={{ ...host, stackComponentID }}>
        <Tag type="grey" component="a" href={href} target="_blank" onClick={handleOpen} css={tagStyle}>
          <span>{href}</span>
          <ArrowSquareOut />
        </Tag>
      </ViewAppBlocker>
    </>
  );
};

const tagStyle = (theme: Theme) => css`
  padding: 2px 6px;
  min-height: 24px;
  gap: 5px;
  background-color: ${theme.color.grey[60]};
  color: ${theme.color.grey[100]} !important;
  text-decoration: none;
  cursor: pointer;
`;

const titleStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: bold !important;
  margin-bottom: 4px;
`;
